/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { Alert, Link } from '@zapier/design-system';
import { useState, useEffect } from 'react';

import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { BannerProps } from './types';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

const ANNUAL_UPDATE_DISMISSED_KEY = 'annualUpsellDismissedOn';

const calculateAnnualSavingsAmount = (data: ResponseData) => {
  const { currentAccount } = data;
  const currentPlan = currentAccount.plan;
  const alternatePlan = data.possiblePlans
    .find((possiblePlan) => {
      return possiblePlan.planType === currentPlan.type;
    })
    ?.plans.find((plan) => {
      return plan.name === currentPlan.name && plan.interval === 'year';
    });
  const currentMonthlyAmount = currentPlan.amount || 0;
  const alternateMonthlyAmount =
    (alternatePlan?.amount || currentMonthlyAmount) / 12;
  const difference = currentMonthlyAmount - alternateMonthlyAmount;
  return difference / currentMonthlyAmount;
};

export const shouldRender = (data: ResponseData) => {
  const {
    currentAccount: { plan },
  } = data;
  // If they're already an annual plan they can't be upsold, and users on legacy
  // plans shouldn't see the banner.
  const isEligibleForAnnualUpsell =
    plan.interval !== 'year' && plan.isInPlanGroup;
  const hasDismissedAnnualUpsell =
    localStorage.getItem(ANNUAL_UPDATE_DISMISSED_KEY) !== null;

  if (hasDismissedAnnualUpsell || !isEligibleForAnnualUpsell) {
    return false;
  }

  return calculateAnnualSavingsAmount(data) > 0;
};

export const Component = function AnnualBillingUpsell(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const [annualUpsellDismissedOn, setAnnualUpsellDismissedOn] = useState(
    localStorage.getItem(ANNUAL_UPDATE_DISMISSED_KEY)
  );

  useEffect(() => {
    if (annualUpsellDismissedOn !== null) {
      localStorage.setItem(
        ANNUAL_UPDATE_DISMISSED_KEY,
        annualUpsellDismissedOn
      );
    }
  }, [annualUpsellDismissedOn]);

  const { data } = props;
  const currentPlan = data.currentAccount.plan;
  const savingsAmount = calculateAnnualSavingsAmount(data);
  const savingsPercent = `${Math.floor(savingsAmount * 100)}%`;
  const upgradeUrl = encodeURI(
    `/app/checkout/change/${currentPlan.name.toLowerCase()}`
  );

  const onUpgrade = () => {
    props.trackEvent('clicked annual billing nudge');
  };

  const onDismiss = () => {
    setAnnualUpsellDismissedOn(new Date().toUTCString());
  };

  const title = (
    <span>
      {`Save ${savingsPercent} when you `}
      <Link href={getFullyQualifiedURL(upgradeUrl)} onClick={onUpgrade}>
        switch to an annual subscription
      </Link>
    </span>
  );

  if (annualUpsellDismissedOn) {
    return null;
  }

  return (
    <Alert
      layout={props.layout}
      title={title}
      type="info"
      onDismiss={onDismiss}
    />
  );
};
